import React, { useMemo } from 'react';
import '../styles/PremiumBehaviourPanel.css';

const fmt  = (v, d = 2) => (v != null ? Number(v).toFixed(d) : '—');
const fmtP = (v)        => (v != null ? (v > 0 ? '+' : '') + Number(v).toFixed(1) + '%' : '—');

const SIGNAL_COLOR = {
  'Long Build-up':  '#2e7d32',
  'Short Covering': '#1565c0',
  'Short Build-up': '#c62828',
  'Long Unwinding': '#e65100',
  'Neutral':        '#888',
};

const EDGE_COLOR = (v) => {
  if (v == null) return '#888';
  if (v >  5) return '#c62828';   // overpriced — seller's edge
  if (v < -5) return '#2e7d32';   // underpriced — buyer's edge
  return '#555';
};

const REC_CONFIG = {
  'CALL BUY': { bg: '#1b5e20', label: 'CALL BUY — Premium supports upside',   emoji: '📈' },
  'PUT BUY':  { bg: '#b71c1c', label: 'PUT BUY — Premium supports downside',   emoji: '📉' },
  'NO TRADE': { bg: '#424242', label: 'NO TRADE — Premium not favourable',      emoji: '🚫' },
};

const PremiumBehaviourPanel = ({ gammaData }) => {
  const {
    spotPrice,
    strikeData = [],
    atmStraddlePrice,
    atmStraddleFairValue,
    straddlePremiumEdge,
    premiumSentiment,
    ivPremiumRatio,
    atmCallIV,
    atmPutIV,
    hasValidData,
    premiumRecommendation,
    premiumRecommendationStrength,
    premiumRecommendationReasons = [],
  } = gammaData || {};

  // ±7 strikes centred on spot, descending
  const rows = useMemo(() => {
    if (!strikeData.length || !spotPrice) return [];
    const sorted = [...strikeData].sort(
      (a, b) => Math.abs(a.strikePrice - spotPrice) - Math.abs(b.strikePrice - spotPrice)
    );
    return sorted.slice(0, 15).sort((a, b) => b.strikePrice - a.strikePrice);
  }, [strikeData, spotPrice]);

  const atmStrike = useMemo(() => {
    if (!strikeData.length) return null;
    return strikeData.reduce((p, c) =>
      Math.abs(c.strikePrice - spotPrice) < Math.abs(p.strikePrice - spotPrice) ? c : p
    ).strikePrice;
  }, [strikeData, spotPrice]);

  if (!gammaData || !hasValidData) return null;

  const sentimentColor =
    premiumSentiment === 'Overpriced'   ? '#c62828' :
    premiumSentiment === 'Underpriced'  ? '#2e7d32' :
    premiumSentiment === 'Fair'         ? '#1565c0' : '#888';

  const skewLabel =
    ivPremiumRatio == null ? '—' :
    ivPremiumRatio > 1.05  ? `CE > PE  (${fmt(ivPremiumRatio, 2)}×)  — Call skew` :
    ivPremiumRatio < 0.95  ? `PE > CE  (${fmt(ivPremiumRatio, 2)}×)  — Put skew` :
    `Balanced  (${fmt(ivPremiumRatio, 2)}×)`;

  const recCfg = REC_CONFIG[premiumRecommendation];

  return (
    <div className="pb-panel">
      <h3 className="pb-title">Premium Behaviour Analysis</h3>

      {/* ── Recommendation banner ── */}
      {recCfg && (
        <div className="pb-rec-banner" style={{ background: recCfg.bg }}>
          <div className="pb-rec-header">
            <span className="pb-rec-emoji">{recCfg.emoji}</span>
            <span className="pb-rec-label">{recCfg.label}</span>
            {premiumRecommendationStrength && (
              <span className="pb-rec-strength">{premiumRecommendationStrength}</span>
            )}
          </div>
          {premiumRecommendationReasons.length > 0 && (
            <ul className="pb-rec-reasons">
              {premiumRecommendationReasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Summary row ── */}
      <div className="pb-summary">
        <div className="pb-card">
          <div className="pb-card-label">ATM Straddle</div>
          <div className="pb-card-value">{fmt(atmStraddlePrice)}</div>
        </div>
        <div className="pb-card">
          <div className="pb-card-label">Fair Straddle (BS)</div>
          <div className="pb-card-value">{fmt(atmStraddleFairValue)}</div>
        </div>
        <div className="pb-card">
          <div className="pb-card-label">Premium Edge</div>
          <div className="pb-card-value" style={{ color: EDGE_COLOR(straddlePremiumEdge) }}>
            {fmtP(straddlePremiumEdge)}
          </div>
        </div>
        <div className="pb-card">
          <div className="pb-card-label">Sentiment</div>
          <div className="pb-card-value pb-pill" style={{ background: sentimentColor }}>
            {premiumSentiment || '—'}
          </div>
        </div>
        <div className="pb-card">
          <div className="pb-card-label">CE IV / PE IV</div>
          <div className="pb-card-value">
            {atmCallIV != null ? fmt(atmCallIV, 1) + '%' : '—'} / {atmPutIV != null ? fmt(atmPutIV, 1) + '%' : '—'}
          </div>
        </div>
        <div className="pb-card">
          <div className="pb-card-label">IV Skew</div>
          <div className="pb-card-value pb-skew">{skewLabel}</div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="pb-legend">
        <span><span className="pb-dot" style={{ background: '#c62828' }}/>Premium Edge &gt;+5% = Overpriced (seller advantage)</span>
        <span><span className="pb-dot" style={{ background: '#2e7d32' }}/>Premium Edge &lt;-5% = Underpriced (buyer opportunity)</span>
        <span><span className="pb-dot" style={{ background: '#2e7d32' }}/>Long Build-up</span>
        <span><span className="pb-dot" style={{ background: '#1565c0' }}/>Short Covering</span>
        <span><span className="pb-dot" style={{ background: '#c62828' }}/>Short Build-up</span>
        <span><span className="pb-dot" style={{ background: '#e65100' }}/>Long Unwinding</span>
      </div>

      {/* ── Strike table ── */}
      <div className="pb-table-wrap">
        <table className="pb-table">
          <thead>
            <tr>
              {/* CE side */}
              <th>CE Signal</th>
              <th>CE Edge%</th>
              <th>CE Fair</th>
              <th>CE LTP</th>
              <th>CE IV%</th>
              {/* Strike */}
              <th className="pb-th-strike">Strike</th>
              {/* PE side */}
              <th>PE IV%</th>
              <th>PE LTP</th>
              <th>PE Fair</th>
              <th>PE Edge%</th>
              <th>PE Signal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(s => {
              const isAtm = s.strikePrice === atmStrike;
              return (
                <tr key={s.strikePrice} className={isAtm ? 'pb-row-atm' : ''}>
                  {/* CE signal */}
                  <td>
                    <span
                      className="pb-signal-pill"
                      style={{ background: SIGNAL_COLOR[s.cePremiumSignal] || '#888' }}
                    >
                      {s.cePremiumSignal || '—'}
                    </span>
                  </td>
                  {/* CE edge */}
                  <td style={{ color: EDGE_COLOR(s.callPremiumEdge), fontWeight: 700 }}>
                    {fmtP(s.callPremiumEdge)}
                  </td>
                  {/* CE fair (theoretical) */}
                  <td className="pb-muted">{fmt(s.callTheoPrice)}</td>
                  {/* CE LTP */}
                  <td className="pb-ltp pb-ce">{fmt(s.ceLTP)}</td>
                  {/* CE IV */}
                  <td className="pb-iv">{s.callIV != null ? fmt(s.callIV, 1) : '—'}</td>

                  {/* Strike */}
                  <td className="pb-strike">
                    {s.strikePrice}
                    {isAtm && <span className="pb-atm-tag">ATM</span>}
                  </td>

                  {/* PE IV */}
                  <td className="pb-iv">{s.putIV != null ? fmt(s.putIV, 1) : '—'}</td>
                  {/* PE LTP */}
                  <td className="pb-ltp pb-pe">{fmt(s.peLTP)}</td>
                  {/* PE fair */}
                  <td className="pb-muted">{fmt(s.putTheoPrice)}</td>
                  {/* PE edge */}
                  <td style={{ color: EDGE_COLOR(s.putPremiumEdge), fontWeight: 700 }}>
                    {fmtP(s.putPremiumEdge)}
                  </td>
                  {/* PE signal */}
                  <td>
                    <span
                      className="pb-signal-pill"
                      style={{ background: SIGNAL_COLOR[s.pePremiumSignal] || '#888' }}
                    >
                      {s.pePremiumSignal || '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PremiumBehaviourPanel;
